/**
 * ST-5 — placing a site tag from a phone, with no account at all.
 *
 * Three screens in the widget's own paper language: who is placing it, what is
 * wrong here, and done. The sticker is the credential; the token the claim
 * returns lives on this phone so a second scanner cannot rewrite the first
 * one's words. Nothing in this flow asks anybody to sign in.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const bare = axios.create();

const WHO_KEY = 'kmTagPlacer';
const tokenKey = (code) => `kmTagToken:${code}`;
export const OFFICE_KEY = 'kmOfficeToken';

const readWho = () => {
  try {
    return JSON.parse(window.localStorage.getItem(WHO_KEY) || '{}') || {};
  } catch {
    return {};
  }
};

export const placerTokenFor = (code) => window.localStorage.getItem(tokenKey(code)) || '';

export default function PlaceByPhone({ tag, code }) {
  const remembered = readWho();
  const office = window.localStorage.getItem(OFFICE_KEY) || '';
  const [asParty, setAsParty] = useState(!!office);
  const [who, setWho] = useState({
    name: remembered.name || '', company: remembered.company || '',
    phone: remembered.phone || '',
  });
  // Where this phone picks up: nowhere yet, in the middle of the words, or at
  // the end if it has already said the work is done.
  const held = placerTokenFor(code);
  const finished = ['done_pending', 'done'].includes(tag.state);
  const [screen, setScreen] = useState(
    held ? (finished ? 'done' : 'what') : 'who');
  const [token, setToken] = useState(placerTokenFor(code));
  const [state, setState] = useState(tag);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [near, setNear] = useState(null);       // null = not asked, {} = nothing
  const [spot, setSpot] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [trades, setTrades] = useState([]);
  const saveTimer = useRef(null);

  // The address, asked for once. A phone that says no is never asked again.
  const askWhere = useCallback(() => {
    if (!navigator.geolocation) { setNear({}); return; }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      setSpot({ lat, lng });
      try {
        const r = await bare.get(`${API}/m/${code}/nearby`, { params: { lat, lng } });
        setNear(r.data || {});
      } catch {
        setNear({});
      }
    }, () => setNear({}), { timeout: 8000 });
  }, [code]);

  useEffect(() => { if (screen === 'what' && near === null) askWhere(); },
    [screen, near, askWhere]);

  const claim = async () => {
    if (!who.name.trim()) { toast.error('Say who you are'); return; }
    setBusy(true);
    try {
      const { data } = await bare.post(`${API}/m/${code}/claim`, {
        name: who.name.trim(), company: who.company.trim(), phone: who.phone.trim(),
        office_token: asParty ? office : '',
      });
      window.localStorage.setItem(WHO_KEY, JSON.stringify(who));
      window.localStorage.setItem(tokenKey(code), data.placer_token);
      setToken(data.placer_token);
      setState(data);
      setScreen('what');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'That did not save');
    } finally {
      setBusy(false);
    }
  };

  const saveNote = (value) => {
    setNote(value);
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      try {
        const { data } = await bare.patch(`${API}/m/${code}`,
          { note: value, placer_token: token });
        setState(data);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'That did not save');
      }
    }, 700);
  };

  const sendPhoto = async (file) => {
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append('file', file);
    form.append('placer_token', token);
    try {
      const { data } = await bare.post(`${API}/m/${code}/photos`, form);
      setState(data);
      toast.success('Photo added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'That photo did not go');
    } finally {
      setBusy(false);
    }
  };

  const confirmAddress = async () => {
    setBusy(true);
    try {
      const { data } = await bare.post(`${API}/m/${code}/project`, {
        project_id: near.project_id, confirm: true, placer_token: token, ...spot,
      });
      setState(data);
      setRooms(data.room_options || []);
      setTrades(data.trade_options || []);
      setNear({ ...near, agreed: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'That did not save');
    } finally {
      setBusy(false);
    }
  };

  const setField = async (field, value) => {
    try {
      const { data } = await bare.patch(`${API}/m/${code}`,
        { [field]: value, placer_token: token });
      setState(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'That did not save');
    }
  };

  const markDone = async () => {
    setBusy(true);
    try {
      const { data } = await bare.post(`${API}/m/${code}/done`, { placer_token: token });
      setState(data);
      setScreen('done');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'That did not save');
    } finally {
      setBusy(false);
    }
  };

  const head = (line) => (
    <div className="kfs-page__hd">
      <span className="kfs-page__k">{tag.firm} · Tag {tag.tag_number}</span>
      <span className="kfs-page__t">{line}</span>
    </div>
  );

  // --- screen 1: who is holding the tag -------------------------------------
  if (screen === 'who') {
    return (
      <div className="kso-wrap">
        <div className="kfs-page" data-testid="tag-place-who">
          {head("Who's placing this tag?")}
          <div className="kfs-page__bd">
            {asParty ? (
              <>
                <div className="kfs-hold" data-testid="tag-place-as-party">
                  Placing as your firm's party, from the Site Office link on this phone.
                </div>
                <button type="button" className="kso-btn" data-testid="tag-place-change"
                  onClick={() => setAsParty(false)}>Not me — change</button>
              </>
            ) : null}
            <div className="kmt-form">
              <label style={{ gridColumn: '1 / -1' }}>Your name
                <input className="kso-in" maxLength={60} value={who.name}
                  onChange={(e) => setWho({ ...who, name: e.target.value })}
                  data-testid="tag-place-name" />
              </label>
              <label style={{ gridColumn: '1 / -1' }}>Company (if any)
                <input className="kso-in" maxLength={60} value={who.company}
                  onChange={(e) => setWho({ ...who, company: e.target.value })}
                  data-testid="tag-place-company" />
              </label>
              <label style={{ gridColumn: '1 / -1' }}>Phone (if they need you)
                <input className="kso-in" maxLength={40} value={who.phone}
                  onChange={(e) => setWho({ ...who, phone: e.target.value })}
                  data-testid="tag-place-phone" />
              </label>
            </div>
            <button type="button" className="kfs-btn kso-btn brand" disabled={busy}
              onClick={claim} data-testid="tag-place-start">
              {busy ? 'One moment…' : 'Place this tag'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- screen 3: done -------------------------------------------------------
  if (screen === 'done') {
    return (
      <div className="kso-wrap">
        <div className="kfs-page" data-testid="tag-place-done">
          {head('Done.')}
          <div className="kfs-hold" data-testid="tag-place-done-line">
            {state.line || "Marked done — waiting for the firm's OK"}
          </div>
          <div className="kfs-page__bd">
            <div className="kso-hint" data-testid="tag-place-another">
              Place another? Scan the next sticker.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- screen 2: what is wrong here ----------------------------------------
  const agreed = state.address_agreed;
  return (
    <div className="kso-wrap">
      <div className="kfs-page" data-testid="tag-place-what">
        {head("What's wrong here?")}
        <div className="kfs-page__bd">
          {near && near.project_id && !agreed ? (
            <div className="kso-row" data-testid="tag-place-nearby">
              <div className="t"><b>At {near.label}?</b></div>
              <button type="button" className="kso-btn brand" disabled={busy}
                onClick={confirmAddress} data-testid="tag-place-nearby-yes">Yes</button>
              <button type="button" className="kso-btn" onClick={() => setNear({})}
                data-testid="tag-place-nearby-no">No</button>
            </div>
          ) : null}

          <label className="kfs-cam" data-testid="tag-place-photo-field">
            <input type="file" accept="image/*" capture="environment" hidden
              onChange={(e) => sendPhoto(e.target.files?.[0])}
              data-testid="tag-place-photo" />
            <span className="kfs-btn kso-btn">
              {busy ? 'Sending…' : 'Take a photo'}
            </span>
          </label>
          {(state.photos || []).length ? (
            <div className="kso-hint" data-testid="tag-place-photo-count">
              {state.photos.length} photo{state.photos.length === 1 ? '' : 's'} on this tag
            </div>
          ) : null}

          <div className="kmt-form">
            <label style={{ gridColumn: '1 / -1' }}>What is wrong
              <textarea className="kso-in" rows={4} maxLength={2000} value={note}
                onChange={(e) => saveNote(e.target.value)}
                data-testid="tag-place-note" />
            </label>
            {agreed && rooms.length ? (
              <label>Room
                <select className="kso-in" value={state.room_id || ''}
                  onChange={(e) => setField('room_id', e.target.value)}
                  data-testid="tag-place-room">
                  <option value="">—</option>
                  {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </label>
            ) : null}
            {agreed && trades.length ? (
              <label>Trade
                <select className="kso-in" value={state.trade || ''}
                  onChange={(e) => setField('trade', e.target.value)}
                  data-testid="tag-place-trade">
                  <option value="">—</option>
                  {trades.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
            ) : null}
          </div>

          <button type="button" className="kfs-btn kso-btn brand" disabled={busy}
            onClick={markDone} data-testid="tag-place-done-btn">Mark done</button>
        </div>
      </div>
    </div>
  );
}
